import axiosIns from '@axiosIns'
import Vue from 'vue'

export const DEFAULT_PAGE_NUMBER = 1
export const DEFAULT_PAGE_SIZE = 20

export default {
    namespaced: true,
    state: {
        requests: [],
        loading: false,
        error: null,
        pagination: {
            currentPage: DEFAULT_PAGE_NUMBER,
            pageSize: DEFAULT_PAGE_SIZE,
            totalCount: 0,
            totalPages: 0,
            hasNext: false,
            hasPrevious: false,
        },
        searchParams: {
            searchTerm: '',
            includeArchived: false,
        },
    },
    getters: {
        requests: state => state.requests,
        loading: state => state.loading,
        error: state => state.error,
        pagination: state => state.pagination,
        searchParams: state => state.searchParams,
    },
    mutations: {
        setRequests(state, requests) {
            state.requests = requests
        },
        setLoading(state, loading) {
            state.loading = loading
        },
        setError(state, error) {
            state.error = error
        },
        setPagination(state, pagination) {
            state.pagination = { ...state.pagination, ...pagination }
        },
        setSearchParams(state, params) {
            state.searchParams = { ...state.searchParams, ...params }
        },
        updateRequest(state, updatedRequest) {
            const index = state.requests.findIndex(r => r.id === updatedRequest.id)
            if (index >= 0) {
                Vue.set(state.requests, index, updatedRequest)
            }
        },
        removeRequest(state, id) {
            state.requests = state.requests.filter(r => r.id !== id)
        },
    },
    actions: {
        async fetchRequests({ commit, state }, params = {}) {
            commit('setLoading', true)
            commit('setError', null)
            try {
                const pageNumber = params.pageNumber ?? state.pagination.currentPage
                const pageSize = params.pageSize ?? state.pagination.pageSize
                const includeArchived = params.includeArchived ?? state.searchParams.includeArchived
                const searchTerm = params.searchTerm ?? state.searchParams.searchTerm

                commit('setSearchParams', { includeArchived, searchTerm })

                const queryParams = new URLSearchParams({
                    pageNumber,
                    pageSize,
                    includeArchived,
                })
                if (searchTerm) {
                    queryParams.append('searchTerm', searchTerm)
                }

                const response = await axiosIns.get(`AccommodationRequest?${queryParams.toString()}`)

                const items = Array.isArray(response.data) ? response.data : response.data?.items ?? []

                // Parse pagination metadata from X-Pagination header
                const paginationHeader = response.headers['x-pagination']
                let paginationData = {
                    currentPage: pageNumber,
                    pageSize,
                    totalCount: items.length,
                    totalPages: Math.ceil(items.length / pageSize),
                    hasNext: false,
                    hasPrevious: false,
                }

                if (paginationHeader) {
                    try {
                        const headerData = JSON.parse(paginationHeader)
                        const totalPages = Math.max(
                            headerData.TotalPages ?? 0,
                            Math.ceil((headerData.TotalCount ?? items.length) / (headerData.PageSize ?? pageSize)),
                        )

                        paginationData = {
                            currentPage: headerData.CurrentPage ?? pageNumber,
                            pageSize: headerData.PageSize ?? pageSize,
                            totalCount: headerData.TotalCount ?? items.length,
                            totalPages,
                            hasNext: headerData.HasNext ?? false,
                            hasPrevious: headerData.HasPrevious ?? false,
                        }
                    } catch (e) {
                        console.error('Failed to parse X-Pagination header:', e)
                    }
                }

                commit('setRequests', items)
                commit('setPagination', paginationData)
            } catch (error) {
                console.error('Error fetching accommodation requests:', error)
                commit('setError', 'Erreur lors du chargement des demandes')
                throw error
            } finally {
                commit('setLoading', false)
            }
        },
        async updateAppointment({ commit, state }, { id, appointmentDateTime }) {
            try {
                await axiosIns.put(`AccommodationRequest/${id}`, { appointmentDateTime })
                const request = state.requests.find(r => r.id === id)
                if (request) {
                    commit('updateRequest', { ...request, appointmentDateTime })
                }
            } catch (error) {
                console.error('Error updating appointment:', error)
                throw error
            }
        },
        async archiveRequest({ commit, state }, id) {
            try {
                const request = state.requests.find(r => r.id === id)
                if (request) {
                    const newStatus = !request.isArchived
                    await axiosIns.put(`AccommodationRequest/${id}/archive-status?isArchived=${newStatus}`)
                    commit('updateRequest', { ...request, isArchived: newStatus })
                }
            } catch (error) {
                console.error('Error archiving request:', error)
                throw error
            }
        },
        async updateFollowUpStatus({ commit, state }, { id, followUpStatus }) {
            try {
                await axiosIns.put(`AccommodationRequest/${id}/follow-up-status`, { followUpStatus })
                const request = state.requests.find(r => r.id === id)
                if (request) {
                    commit('updateRequest', { ...request, followUpStatus })
                }
            } catch (error) {
                console.error('Error updating follow-up status:', error)
                throw error
            }
        },
        async exportRequests(_, { start, end, includeArchived = false }) {
            const response = await axiosIns.post(
                'AccommodationRequest/ExportAccommodationRequest',
                { start, end, includeArchived },
                { responseType: 'blob' },
            )
            return response.data
        },
    },
}
